'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  FolderOpen,
  Plus,
  Trash2,
  Download,
  FileJson,
  FileCode2,
  FileTerminal,
  Upload,
  Pencil,
  Check,
  X,
  Clock,
  Box,
  Sparkles,
  Terminal,
  Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useEquaFlowStore } from '@/lib/store';
import { exportToPython } from '@/lib/export-python';
import {
  listProjects,
  getProject as getProjectFromStorage,
  deleteProject as deleteProjectFromStorage,
  renameProject as renameProjectInStorage,
  areDemosInitialized,
  initializeDemos,
  recoverLatestProjectBackup,
  type ProjectMeta,
} from '@/lib/project-storage';
import { buildAiRoiDemo, buildEcommerceDemo, buildTeamBudgetDemo, buildSaaSGrowthDemo } from '@/lib/demo-builders';
import { exportToDSL as exportToDSLSafe } from '@/lib/dsl-parser';
import { toast } from 'sonner';

// Initialize demo projects at module load time (once per session)
if (typeof window !== 'undefined' && !areDemosInitialized()) {
  initializeDemos(buildEcommerceDemo, buildTeamBudgetDemo, buildSaaSGrowthDemo, buildAiRoiDemo);
}

interface ProjectsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenDSL: () => void;
}

export function ProjectsPanel({ open, onOpenChange, onOpenDSL }: ProjectsPanelProps) {
  const entities = useEquaFlowStore((s) => s.entities);
  const currentProjectId = useEquaFlowStore((s) => s.currentProjectId);
  const currentProjectName = useEquaFlowStore((s) => s.currentProjectName);
  const saveToBrowser = useEquaFlowStore((s) => s.saveToBrowser);
  const loadFromBrowser = useEquaFlowStore((s) => s.loadFromBrowser);
  const createNewProject = useEquaFlowStore((s) => s.createNewProject);
  const setCurrentProject = useEquaFlowStore((s) => s.setCurrentProject);
  const exportProject = useEquaFlowStore((s) => s.exportProject);
  const importProject = useEquaFlowStore((s) => s.importProject);
  const storeExportToDSL = useEquaFlowStore((s) => s.exportToDSL);
  const loadDemo = useEquaFlowStore((s) => s.loadDemo);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [showNewProject, setShowNewProject] = useState(false);
  const [importPreview, setImportPreview] = useState<{ json: string; fileName: string; name: string; entityCount: number; scenarioCount: number } | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compute projects from localStorage — refreshCounter triggers re-reads after mutations
  const projects = useMemo(() => listProjects(), [refreshCounter]);

  // Refresh list helper (used by handlers after mutations)
  const refreshList = useCallback(() => {
    setRefreshCounter((c) => c + 1);
  }, []);

  // ── Handlers ──

  const handleLoadProject = useCallback(
    (id: string) => {
      let success = loadFromBrowser(id);
      let recovered = false;
      if (!success && recoverLatestProjectBackup(id)) {
        recovered = loadFromBrowser(id);
        success = recovered;
      }
      if (success) {
        toast.success(recovered ? 'Project recovered from its local backup' : 'Project loaded');
        refreshList();
      } else {
        toast.error('Project could not be loaded and no valid local backup was available');
      }
    },
    [loadFromBrowser, refreshList],
  );

  const handleSaveCurrent = useCallback(() => {
    const entityCount = Object.keys(entities).length;
    if (entityCount === 0) {
      toast.error('Nothing to save — add some entities first');
      return;
    }
    saveToBrowser();
    const { persistenceStatus, persistenceError } = useEquaFlowStore.getState();
    if (persistenceStatus === 'error') {
      toast.error(persistenceError || 'Could not save this project on the device');
      return;
    }
    toast.success('Project saved on this device');
    refreshList();
  }, [entities, saveToBrowser, refreshList]);

  const handleCreateNew = useCallback(() => {
    const name = newProjectName.trim() || 'Untitled';
    if (!createNewProject(name)) {
      toast.error(useEquaFlowStore.getState().persistenceError || 'Save the current project before creating a new one');
      return;
    }
    setShowNewProject(false);
    setNewProjectName('');
    toast.success(`Created "${name}"`);
    refreshList();
  }, [newProjectName, createNewProject, refreshList]);

  const handleRename = useCallback(
    (id: string) => {
      const name = editName.trim();
      if (!name) return;
      renameProjectInStorage(id, name);
      setEditingId(null);
      // Update store if it's the current project
      if (id === currentProjectId) {
        setCurrentProject(id, name);
      }
      refreshList();
      toast.success('Renamed');
    },
    [editName, currentProjectId, setCurrentProject, refreshList],
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteProjectFromStorage(id);
      if (id === currentProjectId) {
        setCurrentProject(null, 'Untitled');
      }
      setDeleteTarget(null);
      refreshList();
      toast.success('Project deleted');
    },
    [currentProjectId, setCurrentProject, refreshList],
  );

  const handleLoadDemo = useCallback((type: 'ecommerce' | 'team' | 'saas' | 'startup' | 'simple-optimization' | 'multi-criteria' | 'airlock') => {
    loadDemo(type);
    toast.success('Demo loaded');
    refreshList();
  }, [loadDemo, refreshList]);

  // ── Download handlers ──

  const handleDownloadJson = useCallback(
    (projectMeta?: ProjectMeta) => {
      let json: string;
      let filename: string;

      if (projectMeta) {
        const project = getProjectFromStorage(projectMeta.id);
        if (!project) {
          toast.error('Project not found');
          return;
        }
        json = JSON.stringify(project, null, 2);
        filename = `${projectMeta.name.replace(/[^a-zA-Z0-9_-]/g, '_')}.json`;
      } else {
        json = exportProject(currentProjectName);
        filename = `${currentProjectName.replace(/[^a-zA-Z0-9_-]/g, '_')}.json`;
      }

      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON downloaded');
    },
    [exportProject, currentProjectName],
  );

  const handleDownloadPy = useCallback(
    (projectMeta?: ProjectMeta) => {
      let code: string;
      let filename: string;

      if (projectMeta) {
        const project = getProjectFromStorage(projectMeta.id);
        if (!project) {
          toast.error('Project not found');
          return;
        }
        const ents: Record<string, typeof entities[string]> = {};
        for (const e of project.entities) {
          ents[e.id] = e;
        }
        code = exportToPython(ents, projectMeta.name);
        filename = `${projectMeta.name.replace(/[^a-zA-Z0-9_-]/g, '_')}.py`;
      } else {
        code = exportToPython(entities, currentProjectName);
        filename = `${currentProjectName.replace(/[^a-zA-Z0-9_-]/g, '_')}.py`;
      }

      const blob = new Blob([code], { type: 'text/x-python' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Python script downloaded');
    },
    [entities, currentProjectName],
  );

  const handleDownloadDSL = useCallback(
    (projectMeta?: ProjectMeta) => {
      let dsl: string;
      let filename: string;

      if (projectMeta) {
        const project = getProjectFromStorage(projectMeta.id);
        if (!project) {
          toast.error('Project not found');
          return;
        }
        const ents: Record<string, typeof entities[string]> = {};
        for (const e of project.entities) {
          ents[e.id] = e;
        }
        dsl = exportToDSLSafe(ents);
        filename = `${projectMeta.name.replace(/[^a-zA-Z0-9_-]/g, '_')}.eqf`;
      } else {
        dsl = storeExportToDSL();
        filename = `${currentProjectName.replace(/[^a-zA-Z0-9_-]/g, '_')}.eqf`;
      }

      const blob = new Blob([dsl], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('DSL file downloaded');
    },
    [entities, currentProjectName, storeExportToDSL],
  );

  const handleImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        if (text) {
          try {
            const parsed = JSON.parse(text) as { name?: unknown; entities?: unknown; scenarios?: unknown };
            if (!Array.isArray(parsed.entities)) throw new Error('missing entities');
            setImportPreview({
              json: text,
              fileName: file.name,
              name: typeof parsed.name === 'string' ? parsed.name : 'Imported model',
              entityCount: parsed.entities.length,
              scenarioCount: Array.isArray(parsed.scenarios) ? parsed.scenarios.length : 1,
            });
          } catch {
            toast.error('Failed to import — invalid file format');
          }
        }
      };
      reader.readAsText(file);
      e.target.value = '';
    },
    [refreshList],
  );

  const confirmImport = useCallback(() => {
    if (!importPreview) return;
    if (currentProjectId || Object.keys(entities).length > 0) {
      saveToBrowser();
      if (useEquaFlowStore.getState().persistenceStatus === 'error') {
        toast.error(useEquaFlowStore.getState().persistenceError || 'Save the current project before importing');
        return;
      }
    }
    const success = importProject(importPreview.json);
    if (!success) {
      toast.error('Could not import this file. The current project is still available.');
      return;
    }
    const saveFailed = useEquaFlowStore.getState().persistenceStatus === 'error';
    toast[saveFailed ? 'error' : 'success'](saveFailed
      ? 'Model opened, but it could not be saved on this device. Your previous project remains in the project list.'
      : 'Imported as a separate local project');
    setImportPreview(null);
    refreshList();
  }, [importPreview, currentProjectId, entities, saveToBrowser, importProject, refreshList]);

  const startEditing = useCallback((project: ProjectMeta) => {
    setEditingId(project.id);
    setEditName(project.name);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingId(null);
    setEditName('');
  }, []);

  // Format date for display
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <>
      {/* Hidden file input for importing */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this project from your browser. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={importPreview !== null} onOpenChange={(v) => !v && setImportPreview(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import this model?</AlertDialogTitle>
            <AlertDialogDescription>
              {importPreview && <span className="block space-y-1">
                <span className="block font-medium text-foreground">{importPreview.name}</span>
                <span className="block">{importPreview.fileName} · {importPreview.entityCount} model blocks · {importPreview.scenarioCount} scenarios</span>
                <span className="block">It will open as a separate project saved on this device. Your current project is saved first and remains in the project list.</span>
              </span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); confirmImport(); }}>
              Import separate project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sheet */}
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="w-[min(20rem,calc(100vw-1rem))] max-w-none p-0 gap-0">
          {/* Header */}
          <SheetHeader className="px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-emerald-600" />
              <SheetTitle className="text-sm">Projects</SheetTitle>
            </div>
            <SheetDescription className="text-[11px]">
              Manage, import, and export your models
            </SheetDescription>
          </SheetHeader>

          <Separator />

          {/* Current project info + save */}
          <div className="px-4 py-2.5 bg-muted/30">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Current</div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
              <span className="text-sm font-medium truncate flex-1">
                {currentProjectName}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveCurrent}
                className="h-6 text-[10px] px-2"
                disabled={Object.keys(entities).length === 0}
              >
                <Save className="h-3 w-3 mr-1" />
                Save
              </Button>
            </div>
          </div>

          <Separator />

          {/* Quick actions */}
          <div className="px-4 py-2.5 space-y-1.5">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Create & Import</div>

            {/* New project */}
            {showNewProject ? (
              <div className="flex items-center gap-1.5">
                <Input
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateNew();
                    if (e.key === 'Escape') setShowNewProject(false);
                  }}
                  placeholder="Project name..."
                  className="h-7 text-xs flex-1"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCreateNew}
                  className="h-7 w-7 p-0 text-emerald-600"
                  aria-label="Create project"
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowNewProject(false)}
                  className="h-7 w-7 p-0"
                  aria-label="Cancel creating project"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewProject(true)}
                  className="flex-1 h-7 text-xs"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  New Project
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-7 text-xs border-violet-200 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-400 dark:hover:bg-violet-950/30"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" />
                      Demo
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem onClick={() => handleLoadDemo('ecommerce')}>
                      <div>
                        <div className="font-medium text-xs">E-Commerce Funnel</div>
                        <div className="text-[10px] text-muted-foreground">Product pricing, ads, conversion</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleLoadDemo('team')}>
                      <div>
                        <div className="font-medium text-xs">Team Budget</div>
                        <div className="text-[10px] text-muted-foreground">Array employees, sum/mean/min/max</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleLoadDemo('saas')}>
                      <div>
                        <div className="font-medium text-xs">SaaS Growth [t]</div>
                        <div className="text-[10px] text-muted-foreground">Time series, churn, [t] and [t-1]</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleLoadDemo('startup')}>
                      <div>
                        <div className="font-medium text-xs">Startup Financial Model</div>
                        <div className="text-[10px] text-muted-foreground">IF/MAX/MIN, arrays, cash runway</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleLoadDemo('simple-optimization')}>
                      <div>
                        <div className="font-medium text-xs">Cost Optimization</div>
                        <div className="text-[10px] text-muted-foreground">Minimize cost, constraints, Optimize solver</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleLoadDemo('multi-criteria')}>
                      <div>
                        <div className="font-medium text-xs">Portfolio Optimization</div>
                        <div className="text-[10px] text-muted-foreground">Multi-criteria: max return + min risk</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleLoadDemo('airlock')}>
                      <div>
                        <div className="font-medium text-xs">Бизнес-модель Airlock · 2027–2029</div>
                        <div className="text-[10px] text-muted-foreground">Тарифы, продажи, сценарии и финансовый результат</div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Import row */}
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleImport}
                className="flex-1 h-7 text-xs text-muted-foreground"
              >
                <Upload className="h-3.5 w-3.5 mr-1" />
                Import File
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { onOpenChange(false); onOpenDSL(); }}
                className="flex-1 h-7 text-xs text-emerald-600 dark:text-emerald-400"
              >
                <Terminal className="h-3.5 w-3.5 mr-1" />
                Import DSL
              </Button>
            </div>
          </div>

          <Separator />

          {/* Project list */}
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-0.5">
              {projects.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-xs">
                  <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>No saved projects yet</p>
                  <p className="text-[10px] mt-1">Create one or import from a file</p>
                </div>
              ) : (
                projects.map((project) => {
                  const isCurrent = project.id === currentProjectId;
                  const isEditing = editingId === project.id;

                  return (
                    <div
                      key={project.id}
                      className={`group rounded-lg border transition-colors ${
                        isCurrent
                          ? 'border-emerald-300 bg-emerald-50/50 dark:border-emerald-700 dark:bg-emerald-950/20'
                          : 'border-transparent hover:border-border hover:bg-muted/30'
                      }`}
                    >
                      {/* Project name row */}
                      <div className="flex items-center gap-1.5 px-2.5 py-2">
                        {/* Expand/click to load */}
                        <button
                          onClick={() => handleLoadProject(project.id)}
                          className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                        >
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                            isCurrent ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                          }`} />
                          {isEditing ? (
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRename(project.id);
                                if (e.key === 'Escape') cancelEditing();
                              }}
                              onBlur={() => handleRename(project.id)}
                              className="h-5 text-xs border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 flex-1"
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span
                              className={`min-w-0 flex-1 text-xs font-medium truncate ${
                                isCurrent ? 'text-emerald-700 dark:text-emerald-400' : ''
                              }`}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                startEditing(project);
                              }}
                            >
                              {project.name}
                            </span>
                          )}
                        </button>

                        {/* Action buttons */}
                        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          {/* Rename */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => startEditing(project)}
                            className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                            title="Rename"
                            aria-label={`Rename ${project.name}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>

                          {/* Download dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                                title="Download"
                                aria-label={`Download ${project.name}`}
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => handleDownloadJson(project)}>
                                <FileJson className="h-3.5 w-3.5 mr-2" />
                                Export JSON
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadPy(project)}>
                                <FileCode2 className="h-3.5 w-3.5 mr-2" />
                                Export Python
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadDSL(project)}>
                                <FileTerminal className="h-3.5 w-3.5 mr-2" />
                                Export DSL
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>

                          {/* Delete */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(project.id)}
                            className="h-5 w-5 p-0 text-muted-foreground hover:text-red-600"
                            title="Delete"
                            aria-label={`Delete ${project.name}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Meta info */}
                      <div className="flex items-center gap-2 px-4 pb-1.5 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-0.5">
                          <Box className="h-2.5 w-2.5" />
                          {project.entityCount} {project.entityCount === 1 ? 'entity' : 'entities'}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <Clock className="h-2.5 w-2.5" />
                          {formatDate(project.updatedAt)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>

          <Separator />

          {/* Footer with export for current project */}
          <div className="px-4 py-2.5">
            <div className="text-[10px] text-muted-foreground mb-1.5">Export current model</div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadJson()}
                className="h-7 text-[10px] px-2 flex-1"
                disabled={Object.keys(entities).length === 0}
              >
                <FileJson className="h-3 w-3 mr-1" />
                JSON
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadPy()}
                className="h-7 text-[10px] px-2 flex-1"
                disabled={Object.keys(entities).length === 0}
              >
                <FileCode2 className="h-3 w-3 mr-1" />
                .py
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadDSL()}
                className="h-7 text-[10px] px-2 flex-1"
                disabled={Object.keys(entities).length === 0}
              >
                <FileTerminal className="h-3 w-3 mr-1" />
                DSL
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
